import React, { createContext, useContext, useState, useEffect, useRef, useMemo } from 'react';
import { Form } from 'antd';
import { useParams } from 'react-router-dom';

import { useSearchParams } from '@/hooks';
import { useFile } from '@/hooks/v1/useFile';
import { useGetUsers } from '@/hooks/useGetUsers';
import { useGetKimLhc } from '@/hooks/useGetKimLhc';
import { useApplicationContext } from '@/context/v1/Application/context';
import { useGetDownloadVideoURL } from '@/hooks/useGetVideoURL';
import type { State, Methods, UrlParams, Context } from './types';

interface KimLhcProviderProps {
	children: JSX.Element;
}

const KimLhcContext = createContext<Context>({} as Context);

const { useForm } = Form;

export function KimLhcProvider({ children }: Readonly<KimLhcProviderProps>) {
	const [form] = useForm();
	const isStepInitialized = useRef<boolean>(false);
	const { report_id } = useSearchParams<UrlParams>();
	const { file_id } = useParams<{ file_id: string }>();
	const { organization, company } = useApplicationContext();

	const [currentStep, setCurrentStep] = useState<number>(0);
	const [reportId, setReportId] = useState<string | undefined>(report_id);

	const [scores, setScores] = useState<{ man: number; woman: number }>({
		man: 0,
		woman: 0
	});

	const { data: report } = useGetKimLhc({
		file_id: file_id || '',
		company_id: company?.id || '',
		organization_id: organization?.id || ''
	});

	const { file: fileData } = useFile({
		organizationId: organization?.id,
		companyId: company?.id,
		fileId: file_id
	});

	const { data: users } = useGetUsers({
		organization_id: organization?.id || '',
		company_id: company?.id || ''
	});

	const { data: videoUrl } = useGetDownloadVideoURL({
		organization_id: organization?.id || '',
		company_id: company?.id || '',
		file_id: file_id || '',
		original_name: fileData?.data?.original_name || ''
	});

	function getUploadAuthor(userId: string) {
		return users?.find((user) => user.id === userId)?.name;
	}

	const videoInfo = useMemo(() => {
		if (!fileData?.data) return undefined;

		const uploadAuthor = getUploadAuthor(fileData.data.user_id);

		return {
			generated_name: fileData.data.generated_name,
			original_name: fileData.data.original_name,
			upload_date: fileData.data.createdAt,
			upload_author_id: fileData.data.user_id || '',
			upload_author_name: uploadAuthor || ''
		};
	}, [fileData, users]);

	useEffect(() => {
		if (report && !isStepInitialized.current && report_id) {
			setCurrentStep(report.step ?? 0);
			isStepInitialized.current = true;
		}
	}, [report, report_id]);

	function handleOnPreviousStep(): void {
		setCurrentStep((prev) => Math.max(0, prev - 1));
	}

	function handleOnNextStep(): void {
		setCurrentStep((prev) => prev + 1);
	}

	function handleOnSetStep(step: number): void {
		setCurrentStep(step);
	}

	function handleOnSetReportId(id: string): void {
		setReportId(id);
	}

	function handleOnSetPartialScores(scores: { man: number; woman: number }): void {
		setScores(scores);
	}

	const states: State = {
		form,
		file_id,
		currentStep,
		report_id: reportId,
		videoInfo,
		videoUrl,
		scores
	};

	const methods: Methods = {
		onSetStep: handleOnSetStep,
		onNextStep: handleOnNextStep,
		onPreviousStep: handleOnPreviousStep,
		onSetReportId: handleOnSetReportId,
		onSetPartialScores: handleOnSetPartialScores
	};

	const context: Context = {
		...states,
		...methods
	};

	return <KimLhcContext.Provider value={context}>{children}</KimLhcContext.Provider>;
}

export const useKimLhc = () => {
	const context = useContext(KimLhcContext);

	if (!context) {
		throw new Error('useKimLhc must be used within a KimLhcProvider');
	}

	return context;
};
